/**
 * Next-schedule PropertyCard: pick the soonest clock rule and keep its action calls.
 */
import type { ActionJob, ActionJobParams } from 'src/types/requests/schedules/schedules_g2';

export type BluTrvScheduleRule = {
	rule_id?: number;
	enable?: boolean;
	timespec?: string;
	target_C?: number;
	pos?: number;
};

export type NextScheduleRule = {
	enable: boolean;
	hour: number;
	min: number;
	days: number[];
	calls: ActionJob[];
};

export type NextScheduleOccurrence = {
	at: Date;
	hour: number;
	min: number;
	schedule: NextScheduleRule;
};

const WEEKDAY_INDEX: Record<string, number> = {
	SUN: 0,
	MON: 1,
	TUE: 2,
	WED: 3,
	THU: 4,
	FRI: 5,
	SAT: 6,
};

export const parseTimespecClock = (
	timespec: string,
): { hour: number; min: number; days: number[] } | undefined => {
	const parts = timespec.trim().split(/\s+/);
	if (parts.length < 6) return undefined;
	const min = Number(parts[1]);
	const hour = Number(parts[2]);
	if (!Number.isFinite(min) || !Number.isFinite(hour)) return undefined;

	const daysToken = parts[5];
	const days =
		!daysToken || daysToken === '*'
			? [0, 1, 2, 3, 4, 5, 6]
			: daysToken
					.split(',')
					.map((token) => WEEKDAY_INDEX[token] ?? Number(token))
					.filter((day) => Number.isFinite(day) && day >= 0 && day <= 6);

	return {
		hour,
		min,
		days: days.length ? days : [0, 1, 2, 3, 4, 5, 6],
	};
};

const toNextScheduleRule = (
	timespec: string | undefined,
	enable: boolean,
	calls: ActionJob[],
): NextScheduleRule | undefined => {
	if (!timespec) return undefined;
	const clock = parseTimespecClock(timespec);
	if (!clock) return undefined;
	return {
		enable,
		hour: clock.hour,
		min: clock.min,
		days: clock.days,
		calls,
	};
};

export const bluTrvRulesToNextSchedule = (
	rules: BluTrvScheduleRule[] | undefined,
): NextScheduleRule[] => {
	if (!rules?.length) return [];
	return rules.flatMap((rule) => {
		const next = toNextScheduleRule(rule.timespec, rule.enable !== false, [
			{
				method: rule.target_C != null ? 'blutrv.target' : 'blutrv.position',
				params: {
					...(rule.target_C != null ? { target_C: rule.target_C } : {}),
					...(rule.pos != null ? { pos: rule.pos } : {}),
				},
			},
		]);
		return next ? [next] : [];
	});
};

export const schedulesFromBluTrvList = (data: unknown): NextScheduleRule[] => {
	if (!data) return [];
	if (Array.isArray(data)) return bluTrvRulesToNextSchedule(data);
	if (typeof data === 'object' && Array.isArray((data as { rules?: unknown }).rules)) {
		return bluTrvRulesToNextSchedule((data as { rules: BluTrvScheduleRule[] }).rules);
	}
	return [];
};

/** Gen1 clock rules look like `0700-0123456-on`. Sunrise/sunset (`sr`/`ss`) are skipped. */
export const parseGen1ScheduleRule = (rule: string): NextScheduleRule | undefined => {
	const raw = String(rule || '').trim();
	if (!raw || /sr|ss/i.test(raw)) return undefined;
	const parts = raw.split('-');
	if (parts.length < 3) return undefined;
	const time = parts[0];
	if (!/^\d{4}$/.test(time)) return undefined;
	const hour = Number(time.slice(0, 2));
	const min = Number(time.slice(2, 4));
	if (!Number.isInteger(hour) || !Number.isInteger(min) || hour > 23 || min > 59) {
		return undefined;
	}
	const days = parts[1]
		.split('')
		.map(Number)
		.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
	const actionPart = parts.slice(2).join('-');
	const match = actionPart.match(/(?:^|[;@])(on|off)$/i);
	if (!match) return undefined;
	return {
		enable: true,
		hour,
		min,
		days: days.length ? days : [0, 1, 2, 3, 4, 5, 6],
		calls: [{ method: 'Light.Set', params: { on: match[1].toLowerCase() === 'on' } }],
	};
};

export const parseGen1ScheduleRules = (rules: unknown): NextScheduleRule[] => {
	const list = Array.isArray(rules)
		? rules
		: typeof rules === 'string'
			? rules.split(',').filter(Boolean)
			: [];
	return list
		.map((rule) => (typeof rule === 'string' ? parseGen1ScheduleRule(rule) : undefined))
		.filter((rule): rule is NextScheduleRule => Boolean(rule));
};

export const gen1LightSchedulesFromSettings = (
	settings: Record<string, unknown> | undefined,
	channel = 0,
): NextScheduleRule[] => {
	const lights = settings?.lights;
	const block = Array.isArray(lights) ? lights[channel] : undefined;
	if (!block || typeof block !== 'object') return [];
	const enabled = Boolean((block as { schedule?: unknown }).schedule);
	if (!enabled) return [];
	return parseGen1ScheduleRules((block as { schedule_rules?: unknown }).schedule_rules);
};

export const gen1LightScheduleRev = (
	settings: Record<string, unknown> | undefined,
	channel = 0,
): number | undefined => {
	if (!settings) return;
	const lights = settings.lights;
	const block = Array.isArray(lights)
		? (lights[channel] as Record<string, unknown> | undefined)
		: undefined;
	const rawRules = block?.schedule_rules;
	const serialized = Array.isArray(rawRules)
		? rawRules.join(',')
		: typeof rawRules === 'string'
			? rawRules
			: '';
	const key = `${block?.schedule ? 1 : 0}:${serialized}`;
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
	}
	return hash;
};

export const parseScheduleJobs = (
	jobs:
		| Array<{
				enable?: boolean;
				timespec?: string;
				calls?: ActionJob[];
		  }>
		| undefined,
): NextScheduleRule[] => {
	if (!jobs?.length) return [];
	return jobs.flatMap((job) => {
		if (!job.calls?.some((call) => call?.method)) return [];
		const next = toNextScheduleRule(job.timespec, job.enable !== false, job.calls);
		return next ? [next] : [];
	});
};

export const findNextSchedule = (
	schedules: NextScheduleRule[] | undefined,
	now = new Date(),
): NextScheduleOccurrence | undefined => {
	if (!schedules?.length) return undefined;

	let soonest: NextScheduleOccurrence | undefined;

	for (const schedule of schedules) {
		if (schedule.enable === false) continue;
		const { hour, min } = schedule;
		if (!Number.isFinite(hour) || !Number.isFinite(min)) continue;
		const days = schedule.days?.length ? schedule.days : [0, 1, 2, 3, 4, 5, 6];

		for (let add = 0; add < 8; add += 1) {
			const candidate = new Date(now);
			candidate.setSeconds(0, 0);
			candidate.setDate(now.getDate() + add);
			candidate.setHours(hour, min, 0, 0);
			if (candidate.getTime() <= now.getTime()) continue;
			if (!days.includes(candidate.getDay())) continue;
			if (!soonest || candidate.getTime() < soonest.at.getTime()) {
				soonest = { at: candidate, hour, min, schedule };
			}
			break;
		}
	}

	return soonest;
};

export type NextScheduleAction =
	| { kind: 'position'; percent: number }
	| { kind: 'target'; celsius: number }
	| { kind: 'boost'; durationMin?: number }
	| { kind: 'on' }
	| { kind: 'off' }
	| { kind: 'toggle' }
	| { kind: 'open' }
	| { kind: 'close' }
	| { kind: 'coverPosition'; percent: number };

export type NextScheduleActionCopy = {
	open: string;
	close: string;
	on: string;
	off: string;
	toggle: string;
	boost: string;
	min: string;
};

const unwrapScheduleCall = (call: ActionJob): { method: string; params: ActionJobParams } => {
	const method = (call?.method || '').toLowerCase();
	const params = call?.params || {};
	if (method === 'blutrv.call' && typeof params.method === 'string') {
		return {
			method: params.method.replace(/trv\.set/i, 'blutrv.').toLowerCase(),
			params: params.params || params,
		};
	}
	return { method, params };
};

const pushPositionAction = (actions: NextScheduleAction[], percent: number) => {
	if (!Number.isFinite(percent)) return;
	actions.push({ kind: 'position', percent });
};

export const parseNextScheduleActions = (calls: ActionJob[] | undefined): NextScheduleAction[] => {
	if (!calls?.length) return [];
	const actions: NextScheduleAction[] = [];

	for (const call of calls) {
		const { method, params } = unwrapScheduleCall(call);
		if (/^(blu)?trv\.(target|position|boost|ext_t)$/.test(method)) {
			if (typeof params.target_C === 'number') {
				actions.push({ kind: 'target', celsius: params.target_C });
			}
			if (typeof params.pos === 'number') {
				pushPositionAction(actions, params.pos);
			}
			if (/boost/.test(method) && typeof params.duration === 'number') {
				actions.push({ kind: 'boost', durationMin: params.duration });
			}
			continue;
		}

		if (/cover/i.test(method)) {
			const state = method.split('.')[1] || '';
			if (state === 'gotoposition' && typeof params.pos === 'number') {
				actions.push({ kind: 'coverPosition', percent: params.pos });
			} else if (state === 'open') {
				actions.push({ kind: 'open' });
			} else if (state === 'close') {
				actions.push({ kind: 'close' });
			}
			continue;
		}

		if (/\.toggle$/i.test(method)) {
			actions.push({ kind: 'toggle' });
			continue;
		}

		const on = params.params?.on ?? params.on ?? params.output;
		if (typeof on === 'boolean') {
			actions.push({ kind: on ? 'on' : 'off' });
		}
	}

	return actions;
};

const formatOpenPercent = (percent: number, copy: NextScheduleActionCopy): string => {
	if (percent <= 0) return copy.close;
	if (percent >= 100) return copy.open;
	return `${copy.open} ${percent}%`;
};

export const formatNextScheduleAction = (
	action: NextScheduleAction,
	copy: NextScheduleActionCopy,
	formatTarget: (celsius: number) => string,
): string => {
	switch (action.kind) {
		case 'position':
		case 'coverPosition':
			return formatOpenPercent(action.percent, copy);
		case 'target':
			return formatTarget(action.celsius);
		case 'boost':
			return action.durationMin != null
				? `${copy.boost} ${action.durationMin} ${copy.min}`
				: copy.boost;
		case 'on':
			return copy.on;
		case 'off':
			return copy.off;
		case 'toggle':
			return copy.toggle;
		case 'open':
			return copy.open;
		case 'close':
			return copy.close;
	}
};

export const formatNextScheduleValue = (timeLabel: string, phrases: string[]): string => {
	const action = phrases.filter((phrase) => phrase.trim() !== '').join(', ');
	return action ? `${timeLabel} - ${action}` : timeLabel;
};

export const formatNextScheduleTarget = (celsius: number, unit?: string): string => {
	if (unit === 'F') {
		return `${Math.round((celsius * 9) / 5 + 32)}°F`;
	}
	return `${celsius}°C`;
};

export const nextScheduleActionIcon = (actions: NextScheduleAction[]): string => {
	switch (actions[0]?.kind) {
		case 'position':
			return 'fa-solid fa-pipe-valve';
		case 'target':
			return 'fa-solid fa-temperature-three-quarters';
		case 'boost':
			return 'fa-solid fa-fire';
		case 'on':
			return 'fa-solid fa-toggle-on';
		case 'off':
			return 'fa-solid fa-toggle-off';
		case 'toggle':
			return 'fa-solid fa-toggle-on';
		case 'open':
		case 'coverPosition':
			return 'fa-regular fa-blinds-raised';
		case 'close':
			return 'fa-regular fa-blinds';
		default:
			return 'fa-solid fa-clock';
	}
};
